// src/pages/enrollments/EnrollmentsPage.jsx
import React, { useState, useEffect } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import StatsCard from '../../components/common/StatsCard';
import Table from '../../components/common/Table';
import Button from '../../components/common/Button';
import PatientNameLink from '../../components/common/PatientNameLink';
import ProgramFilter from '../../components/common/ProgramFilter';
import { useMedicalCenter } from '../../context/MedicalCenterContext';
import { usePermissions } from '../../hooks/usePermissions';
import { usePrograms } from '../../hooks/usePrograms';
import { enrollmentService } from '../../services/enrollmentService';
import { patientService } from '../../services/patientService';
import EnrollmentForm from '../../components/forms/EnrollmentForm';

import { formatDateOnly } from '../../utils/dateUtils';
import { getStatusColor } from '../../utils/statusUtils';
import { useAlert } from '../../context/AlertContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const EnrollmentsPage = () => {
  const { medicalCenterId } = useMedicalCenter();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const { push } = useAlert();
  const { programs, selectedProgramId, setSelectedProgramId } = usePrograms(medicalCenterId);

  const [enrollments, setEnrollments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);

  // Confirm dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const loadEnrollments = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!medicalCenterId) return;

      const enrollmentsData = await enrollmentService.getEnrollments(
        medicalCenterId,
        selectedProgramId || null
      );
      const patientsData = await patientService.getPatients(medicalCenterId);

      setEnrollments(enrollmentsData || []);
      setPatients(patientsData);

      const calculatedStats = enrollmentService.calculateStats(enrollmentsData || []);
      setStats(calculatedStats);
    } catch (err) {
      console.error('Error loading enrollments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!medicalCenterId) return;
    loadEnrollments();
  }, [medicalCenterId, selectedProgramId]);

  const handleAddEnrollment = () => setIsAddModalOpen(true);

  const handleEditEnrollment = (enrollment) => {
    if (!enrollment.patient) {
      push('Patient data not found for this enrollment.', 'warning');
      return;
    }
    setSelectedEnrollment(enrollment);
    setIsEditModalOpen(true);
  };

  const handleDeleteEnrollment = (enrollment) => {
    if (!canDelete('enrollments')) {
      push('You do not have permission to delete enrollments', 'warning');
      return;
    }
    setPendingDelete(enrollment);
    setShowDeleteDialog(true);
  };

  const confirmDeleteEnrollment = async () => {
    try {
      await enrollmentService.deleteEnrollment(pendingDelete.id, medicalCenterId);
      const next = enrollments.filter(e => e.id !== pendingDelete.id);
      setEnrollments(next);
      const nextStats = enrollmentService.calculateStats(next);
      setStats(nextStats);
      push('Enrollment deleted successfully!', 'success');
    } catch (err) {
      console.error('Error deleting enrollment:', err);
      push(`Failed to delete enrollment: ${err.message}`, 'error');
    } finally {
      setShowDeleteDialog(false);
      setPendingDelete(null);
    }
  };

  const handleSubmitEnrollment = async (enrollmentData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const created = await enrollmentService.createEnrollment(enrollmentData, medicalCenterId);
      setIsAddModalOpen(false);

      const basePatient =
        patients.find(p => p.id === enrollmentData.patient_id) || created?.patient || null;

      const createdEnrollment = {
        ...created,
        patient: basePatient || created?.patient || null,
      };

      if (!selectedProgramId || enrollmentData.program_id === selectedProgramId) {
        const next = [createdEnrollment, ...enrollments];
        setEnrollments(next);
        const nextStats = enrollmentService.calculateStats(next);
        setStats(nextStats);
      }

      push('Enrollment created successfully!', 'success');
    } catch (err) {
      console.error('Error adding enrollment:', err);
      setError(err.message);
      push(`Failed to add enrollment: ${err.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEditEnrollment = async (enrollmentData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const updated = await enrollmentService.updateEnrollment(
        selectedEnrollment.id,
        enrollmentData,
        medicalCenterId
      );

      setIsEditModalOpen(false);
      setSelectedEnrollment(null);

      const next = enrollments.map(e => {
        if (e.id !== updated.id) return e;
        return {
          ...e,
          ...updated,
          patient: e.patient || updated.patient || null,
        };
      });

      setEnrollments(next);
      const nextStats = enrollmentService.calculateStats(next);
      setStats(nextStats);

      push('Enrollment updated successfully!', 'success');
    } catch (err) {
      console.error('Error updating enrollment:', err);
      setError(err.message);
      push(`Failed to update enrollment: ${err.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderEnrollmentRow = (enrollment) => {
    const displayStatus =
      enrollment.status === 'completed' || enrollment.status === 'inactive'
        ? 'inactive'
        : enrollment.status;

    return (
      <tr key={enrollment.id} className="hover:bg-gray-50">
        <td className="table-cell font-medium text-gray-900">
          <PatientNameLink
            patient={enrollment.patient}
            enrollment={enrollment}
            className="font-medium text-gray-900 hover:text-blue-600"
          />
        </td>
        <td className="table-cell text-gray-500">{enrollment.patient?.email}</td>
        <td className="table-cell text-gray-500">{enrollment.program?.name || '—'}</td>
        <td className="table-cell text-gray-500">{formatDateOnly(enrollment.start_date)}</td>
        <td className="table-cell">
          <span
            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
              displayStatus,
              'enrollment'
            )}`}
          >
            {displayStatus
              ? displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)
              : 'Unknown'}
          </span>
        </td>
        <td className="table-cell">
          <div className="flex space-x-2">
            {canEdit('enrollments') && (
              <Button size="sm" variant="outline" onClick={() => handleEditEnrollment(enrollment)}>
                Edit
              </Button>
            )}
            {canDelete('enrollments') && (
              <Button size="sm" variant="danger" onClick={() => handleDeleteEnrollment(enrollment)}>
                Delete
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  if (!medicalCenterId) {
    return <div className="p-6 text-gray-600">Select a medical center to continue.</div>;
  }

  if (loading) return <LoadingSpinner className="h-64" />;
  if (error) return <ErrorDisplay error={error} onRetry={loadEnrollments} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Enrollments"
        subtitle="Manage patient enrollments and program participation"
        actionText="Enroll Patient"
        onAction={handleAddEnrollment}
        showAction={canCreate('enrollments')}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard title="Total Enrollments" value={stats.totalEnrollments || 0} subtitle="All enrollments" />
        <StatsCard title="Active Enrollments" value={stats.activeEnrollments || 0} subtitle="Currently active" />
        <StatsCard title="Inactive Enrollments" value={stats.inactiveEnrollments || 0} subtitle="Not active" />
        <StatsCard title="Completion Rate" value={`${stats.completionRate || 0}%`} subtitle="Success rate" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Enrollment List</h2>
          <ProgramFilter
            programs={programs}
            selectedProgramId={selectedProgramId}
            onChange={setSelectedProgramId}
          />
        </div>
        <Table
          headers={['Patient Name', 'Email', 'Program', 'Enrollment Date', 'Status', 'Actions']}
          data={enrollments}
          renderRow={renderEnrollmentRow}
          loading={loading}
          emptyMessage="No enrollments found"
        />
      </div>

      <EnrollmentForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleSubmitEnrollment}
        loading={isSubmitting}
        patients={patients}
      />

      <EnrollmentForm
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedEnrollment(null);
        }}
        onSubmit={handleSubmitEditEnrollment}
        loading={isSubmitting}
        enrollment={selectedEnrollment}
        patients={patients}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setPendingDelete(null);
        }}
        onConfirm={confirmDeleteEnrollment}
        title="Delete Enrollment"
        message="Are you sure you want to delete this enrollment?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default EnrollmentsPage;